import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, requiredPayloadEnvelope } from '../envelope.js';
/** `@lunacast/api-communications` 이벤트 카탈로그 id와 1:1로 맞춘다. */
const communicationEventIdSchema = z.enum([
    'auth.signup.verification',
    'auth.login.verification',
    'auth.admin_mfa.verification',
    'auth.email_bind.verification',
]);
/** 통신 이벤트 미리보기·테스트 발송 폼의 필드 메타다. */
const fieldMetaSchema = z.object({
    /** 템플릿 데이터 객체의 키 이름이다. */
    name: z.string(),
    /** 폼 검증·렌더에 쓰는 값 타입이다. */
    type: z.enum(['string', 'number']),
    /** admin 콘솔 폼에 보이는 라벨이다. */
    label: z.string(),
    /** admin 콘솔에서 그릴 입력 위젯 종류다. */
    widget: z.enum(['input', 'textarea', 'number']),
    /** true면 필드를 비워도 된다. */
    optional: z.boolean(),
});
/** 최근 발송량이 중앙값 대비 이상하면 `high`, 저조하면 `low`다. 관측 7일 미만·비활성·중앙값 0이면 null이다. */
const volumeWarningSchema = z.enum(['high', 'low']).nullable();
/** 이벤트별 발송·차단·실패 집계다. KST 달력일 기준이다. */
const eventStatsSchema = z.object({
    /** 오늘(KST) 성공 발송 건수다. */
    sentToday: z.number().int(),
    /** 최근 7일 성공 발송 건수다. */
    sent7d: z.number().int(),
    /** 최근 30일 성공 발송 건수다. */
    sent30d: z.number().int(),
    /** 최근 30일 서킷·정책으로 차단된 건수다. */
    blocked30d: z.number().int(),
    /** 최근 30일 프로바이더·전송 실패 건수다. */
    failed30d: z.number().int(),
    /** 최근 30일 admin 테스트 발송 건수다. */
    testSent30d: z.number().int(),
    /** 일일 발송량이 중앙값 대비 비정상이면 `high` 또는 `low`다. 없으면 null이다. */
    volumeWarning: volumeWarningSchema,
    /** SendLog 최초 관측일부터의 KST 경과일 수다. 로그가 없으면 0이다. */
    observationDayCount: z.number().int(),
});
/** 통신 이벤트 목록 한 행이다. */
const eventListItemSchema = z.object({
    /** 이벤트 카탈로그 id다. */
    id: communicationEventIdSchema,
    /** v1은 이메일만 지원한다. */
    channel: z.literal('email'),
    /** admin 콘솔 목록 제목이다. */
    title: z.string(),
    /** 운영자용 이벤트 설명이다. */
    description: z.string(),
    /** false면 실발송·테스트 발송이 차단된다. */
    enabled: z.boolean(),
    /** DB 제어 행과 카탈로그가 일치하면 true다. 불일치 시 토글을 막는다. */
    controlHealthy: z.boolean(),
    /** 기간별 발송 통계다. */
    stats: eventStatsSchema,
});
/** 이벤트 상세 — 미리보기·테스트 발송 폼에 쓰는 필드 정의를 포함한다. */
const eventDetailSchema = eventListItemSchema.extend({
    /** 미리보기 폼 필드 메타 목록이다. */
    fields: z.array(fieldMetaSchema),
    /** 미리보기·테스트 발송 폼의 초기값이다. */
    defaultValues: z.record(z.string(), z.unknown()),
    /** 제어 행 마지막 수정 시각이다. 없으면 null이다. */
    updatedAt: z.string().nullable(),
    /** 마지막으로 제어를 바꾼 AdminUser id다. 없으면 null이다. */
    updatedByAdminId: z.string().nullable(),
});
const c = initContract();
/** admin-api — 이메일 통신 이벤트 카탈로그 조회·미리보기·테스트 발송·발송 on/off 제어. */
export const adminCommunicationsContract = c.router({
    /** 등록된 통신 이벤트 목록과 통계를 조회한다. */
    list: {
        method: 'GET',
        path: '/communications/events',
        responses: {
            200: requiredPayloadEnvelope(z.array(eventListItemSchema)),
            ...commonErrorResponses,
        },
    },
    /** 한 이벤트의 상세·폼 필드·기본값을 조회한다. */
    get: {
        method: 'GET',
        path: '/communications/events/:eventId',
        pathParams: z.object({
            /** 조회할 이벤트 id다. */
            eventId: communicationEventIdSchema,
        }),
        responses: {
            200: requiredPayloadEnvelope(eventDetailSchema),
            ...commonErrorResponses,
        },
    },
    /** 템플릿 데이터로 제목·HTML·텍스트 본문을 렌더한다. 실제 발송은 하지 않는다. */
    preview: {
        method: 'POST',
        path: '/communications/events/:eventId/preview',
        pathParams: z.object({
            /** 미리볼 이벤트 id다. */
            eventId: communicationEventIdSchema,
        }),
        body: z.object({
            /** 템플릿에 넣을 필드 값 맵이다. */
            data: z.record(z.string(), z.unknown()),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 렌더된 이메일 제목이다. */
                subject: z.string(),
                /** 렌더된 HTML 본문이다. */
                html: z.string(),
                /** 렌더된 평문 본문이다. */
                text: z.string(),
            })),
            ...commonErrorResponses,
        },
    },
    /** 지정 주소로 테스트 이메일을 발송한다. 쿨다운·서킷 정책을 따른다. */
    testSend: {
        method: 'POST',
        path: '/communications/events/:eventId/test-send',
        pathParams: z.object({
            /** 테스트 발송할 이벤트 id다. */
            eventId: communicationEventIdSchema,
        }),
        body: z.object({
            /** 수신 이메일 주소다. */
            to: z.string().email(),
            /** 템플릿에 넣을 필드 값 맵이다. */
            data: z.record(z.string(), z.unknown()),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 실제 발송에 쓰인 제목이다. */
                subject: z.string(),
                /** 사용한 발송 프로바이더다. `console`은 로컬·dev 스텁이다. */
                provider: z.enum(['console', 'acs']),
            })),
            ...commonErrorResponses,
        },
    },
    /** 이벤트 실발송 on/off를 바꾼다. */
    updateControl: {
        method: 'PATCH',
        path: '/communications/events/:eventId/control',
        pathParams: z.object({
            /** 제어할 이벤트 id다. */
            eventId: communicationEventIdSchema,
        }),
        body: z.object({
            /** true면 실발송을 허용하고, false면 차단한다. */
            enabled: z.boolean(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 갱신된 이벤트 id다. */
                id: communicationEventIdSchema,
                /** 적용된 활성 여부다. */
                enabled: z.boolean(),
                /** 제어 행이 카탈로그와 일치하는지 여부다. */
                controlHealthy: z.boolean(),
                /** 제어 행 수정 시각이다. 없으면 null이다. */
                updatedAt: z.string().nullable(),
                /** 수정한 AdminUser id다. 없으면 null이다. */
                updatedByAdminId: z.string().nullable(),
            })),
            ...commonErrorResponses,
        },
    },
});
export { communicationEventIdSchema, eventDetailSchema, eventListItemSchema, eventStatsSchema, fieldMetaSchema, };
//# sourceMappingURL=communications.contract.js.map